<template>
  <div
    class="AppBarDocumentSearch d-flex flex-grow-1"
    data-locator="document-search"
  >
    <div ref="activatorRef" class="AppBarDocumentSearch__activator">
      <AppGlobalSearch
        v-model="searchQuery"
        :loading="isLoading"
        :placeholder="$t('common.app-bar.AppBarDocumentSearch.placeholder')"
        @focus="fetchRecentFiles"
        @enter="onEnterPressed"
        data-locator="document-search-input"
      />
    </div>

    <AppBarDocumentSearchResults
      ref="resultMenuRef"
      :activator="activatorRef"
      :has-searched="hasSearched"
      :query="searchQuery"
    />
  </div>
</template>

<script setup lang="ts">
import { debounce } from 'lodash-es'
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { SEARCH_INPUT_DEBOUNCE_TIMER } from '@/common/constants'
import { toast } from '@/common/utils/toast'
import AppGlobalSearch from '@/design-system/AppGlobalSearch.vue'
import { GLOBAL_SEARCH_ROUTE_NAME, PROJECT_SEARCH_ROUTE_NAME } from '@/router/routerData'
import { SEARCH_FILES } from '@/store/modules/documents/action_types'

import AppBarDocumentSearchResults from './AppBarDocumentSearchResults.vue'

const store = useStore()
const router = useRouter()
const { t } = useI18n()
const searchQuery = ref('')
const activatorRef = ref<HTMLElement>()
const hasSearched = ref(false)
const resultMenuRef = ref<InstanceType<typeof AppBarDocumentSearchResults>>()

const isLoading = computed(() => store.state.documents.searchFilesPending)
const searchPageName = computed(() =>
  (store.getters['room/roomMnemo']
    ? PROJECT_SEARCH_ROUTE_NAME
    : GLOBAL_SEARCH_ROUTE_NAME
  ),
)


const fetchRecentFiles = async (): Promise<void> => {
  const query = searchQuery.value?.trim()
  if (!query) {
    if (resultMenuRef.value) {
      resultMenuRef.value.openMenu()
    }

    await searchFiles('')
    return
  }

  // If user has already searched, reopen the menu with the current results
  if (hasSearched.value && resultMenuRef.value) {
    resultMenuRef.value.openMenu()
  }
}

const debouncedSearch = debounce(() => {
  const query = searchQuery.value?.trim()
  if (!query) return
  return searchFiles(query)
}, SEARCH_INPUT_DEBOUNCE_TIMER)

async function searchFiles(query: string) {
  try {
    await store.dispatch(`documents/${SEARCH_FILES}`, {
      query,
      page: 1,
      pageSize: 15,
    })

    hasSearched.value = true
    if (query === '') {
      resultMenuRef.value?.openMenu()
    }
  } catch (error) {
    toast.error(t('common.app-bar.AppBarDocumentSearch.searchFilesError'))
  }
}
const onEnterPressed = () => {
  if (!searchQuery.value?.trim()) return
  router.push({
    name: searchPageName.value,
    query: { query: searchQuery.value?.trim() },
  })
  if (resultMenuRef.value) {
    resultMenuRef.value.closeMenu()
  }
}

watch(searchQuery, async (newVal) => {
  const query = newVal?.trim()

  if (!query) {
    hasSearched.value = false
    store.commit('documents/SET_SEARCH_FILES_RESULTS', [])
    await fetchRecentFiles()
    return
  }

  debouncedSearch()
})
</script>

<style scoped lang="scss">
.AppBarDocumentSearch {
  position: relative;
  max-width: 560px;
  min-width: 160px;
}

.AppBarDocumentSearch__activator {
  display: inline-block;
  width: 100%;
}
</style>
